import { type PodcastEpisodeCMS } from "@repo/common/src/types/firestore/cms/podcastEpisode";
import { CMSCollections } from "@repo/common/src/types/firestore/collections";
import { StoragePaths } from "@repo/common/src/types/storage/storagePaths";
import {
  type EntityReference,
  buildCollection,
  buildEntityCallbacks,
  buildProperty,
  type EntityOnSaveProps,
} from "@firecms/core";
import { getFirestore } from "firebase/firestore";

import { Groups } from "./types";
import { standardPermissions } from "../utils/constants";
import { episodeNumberForTitle } from "../utils/episodeNumberFromFeed";
import { generateUniqueSlug } from "../utils/generateUniqueSlug";
import { loadingOverlay } from "../utils/loadOverlay";

type PodcastEpisodeCMSModel = Omit<PodcastEpisodeCMS, "author"> & { author: EntityReference };

const callbacks = buildEntityCallbacks({
  onPreSave: async (props: EntityOnSaveProps<PodcastEpisodeCMSModel>) => {
    loadingOverlay.show();
    const { values, entityId } = props;

    if (values.title) {
      const baseSlug = values.slug || values.title;
      values.slug = await generateUniqueSlug(getFirestore(), CMSCollections.PodcastEpisodes, baseSlug, entityId);

      try {
        const episodeNumber = await episodeNumberForTitle(values.title);
        values.episodeNumber = episodeNumber ?? "";
      } catch {
        // Leave the stored number when the feed cannot be read. Do not block the save.
      }
    }

    return values;
  },
  onSaveSuccess: async () => {
    loadingOverlay.hide();
  },
  onSaveFailure: async () => {
    loadingOverlay.hide();
  },
});

export const podcastEpisodesCollection = buildCollection<PodcastEpisodeCMSModel>({
  id: CMSCollections.PodcastEpisodes,
  name: "Podcast Folgen",
  singularName: "Podcast Folge",
  path: CMSCollections.PodcastEpisodes,
  icon: "mic",
  group: Groups.Content,
  permissions: () => {
    return standardPermissions;
  },
  callbacks,
  properties: {
    published: {
      name: "Veröffentlicht",
      dataType: "boolean",
      description: "Sichtbarkeit des Blog Posts",
    },
    date: {
      name: "Veröffentlichungsdatum",
      validation: { required: true },
      dataType: "date",
    },
    title: {
      name: "Title",
      validation: { required: true },
      dataType: "string",
    },
    slug: {
      name: "URL-freundlicher Slug. Wird automatisch generiert, wenn leer.",
      dataType: "string",
    },
    episodeNumber: {
      name: "Folgennummer",
      description: "Wird beim Speichern aus dem LetsCast-Feed übernommen (itunes:episode, exakter Titel).",
      dataType: "string",
      readOnly: true,
    },
    title_seo: {
      name: "SEO Title",
      validation: { required: true, max: 60 },
      dataType: "string",
    },
    description_seo: {
      name: "SEO Description",
      multiline: true,
      dataType: "string",
      validation: { required: true, max: 240 },
      columnWidth: 300,
    },
    title_image: buildProperty({
      name: "Title Image. Bitte in 1080x1080 hochladen.",
      dataType: "string",
      validation: { required: true },
      storage: {
        storagePath: StoragePaths.PodcastEpisodesImages,
        acceptedFiles: ["image/*"],
        maxSize: 1 * 1024 * 1024, // 1 MB in bytes
        imageCompression: {
          maxWidth: 1080,
          maxHeight: 1080,
        },
      },
    }),
    download_title: {
      name: "Download Name",
      dataType: "string",
    },
    download_link: buildProperty({
      name: "Download Link",
      dataType: "string",
      storage: {
        storagePath: StoragePaths.PodcastEpisodesDownloads,
        acceptedFiles: ["application/pdf", "audio/mpeg"],
        maxSize: 100 * 1024 * 1024, // 100 MB in bytes
      },
    }),
    author: {
      dataType: "reference",
      name: "Author",
      validation: { required: true },
      path: CMSCollections.Authors,
    },
    text: {
      name: "Beschreibung",
      dataType: "string",
      validation: { required: true },
      markdown: true,
    },
    apple_link: {
      name: "Apple Link",
      dataType: "string",
    },
    spotify_link: {
      name: "Spotify Link",
      dataType: "string",
    },
    created_on: buildProperty({
      name: "Erstellungsdatum",
      dataType: "date",
      autoValue: "on_create",
    }),
    updated_on: buildProperty({
      name: "Aktualisierungsdatum",
      dataType: "date",
      autoValue: "on_update",
    }),
  },
});
